import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ThemeToggle } from '../../../shared/theme/ThemeToggle';
import { useIdentity } from '../../identity/state/useIdentity';
import { PARTNER_NAV } from '../partnerNav';
import { isSyntheticPartnerData } from '../services';
import { usePartner } from '../state/usePartner';
import { PARTNER_ROLE_LABELS } from '../types';
import { ClientWorkingBanner } from './ClientWorkingBanner';
import { SyntheticBanner } from './Unavailable';
import '../partnerPortal.css';

/** The portal's own frame: a sidebar from the nav registry (unbuilt sections are shown as not available, never as working), a top bar and the Synthetic Demo Data banner in mock mode. */
export function PartnerShell({ children }: { children: ReactNode }) {
  const { membership } = usePartner();
  const { me, logout } = useIdentity();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  // An item is current only when its whole address (path, query and fragment) is the one shown, so two items that share a page (All Clients and Authorization Requests) never both highlight.
  const current = `${location.pathname}${location.search}${location.hash}`;

  return (
    <div className="pp-shell">
      <a className="pp-skip" href="#pp-main">Skip to content</a>
      <header className="pp-topbar">
        <button type="button" className="pp-menu-button" aria-expanded={open} aria-controls="pp-sidebar" onClick={() => setOpen((v) => !v)}>
          Menu
        </button>
        <div className="pp-brand">
          <span className="pp-brand-name">{membership.legalName}</span>
          <span className="pp-brand-sub">Partner Portal · {PARTNER_ROLE_LABELS[membership.role]}</span>
        </div>
        <div className="pp-topbar-right">
          <ThemeToggle />
          <span className="pp-user" title={me?.email}>{me?.displayName ?? me?.email}</span>
          <button type="button" className="pp-link-button" onClick={() => void logout()}>Sign out</button>
        </div>
      </header>

      {/* While working inside a client, this stays at the top of every page: the client, the firm, the role and Exit Client. */}
      <ClientWorkingBanner />

      <div className="pp-body">
        <nav id="pp-sidebar" aria-label="Partner Portal" className={`pp-sidebar${open ? ' pp-sidebar--open' : ''}`}>
          {PARTNER_NAV.map((group) => (
            <div key={group.label} className="pp-nav-group">
              <h2 className="pp-nav-heading">{group.label}</h2>
              <ul>
                {group.items.map((item) => (
                  <li key={item.label}>
                    {item.to ? (
                      <Link to={item.to} className={`pp-nav-item${item.to === current ? ' pp-nav-item--active' : ''}`} aria-current={item.to === current ? 'page' : undefined} onClick={() => setOpen(false)}>
                        {item.label}
                      </Link>
                    ) : (
                      <span className="pp-nav-item pp-nav-item--off" aria-disabled="true" title={item.reason}>
                        {item.label}
                        <span className="pp-nav-note">Not available yet</span>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <main id="pp-main" className="pp-main" tabIndex={-1}>
          {isSyntheticPartnerData && <SyntheticBanner />}
          {children}
        </main>
      </div>
    </div>
  );
}
